import { useMemo } from 'react';

import isEmpty from 'lodash/isEmpty';
import { LongitudinalQuestionResult, TapAnswer } from '../../../../api';
import useAsset from '../../../hook/useAsset';

interface FormatResultsDataProps {
  result: LongitudinalQuestionResult['attributes'];
  questionType: number;
  tapAnswers: TapAnswer[];
  chartWaveLabels: Record<string, string>;
  usePercentage: boolean;
}

export function formatResultsData(
  answerTypes: number[],
  result: LongitudinalQuestionResult['attributes'],
  tapAnswers: TapAnswer[],
  questionType: number,
  chartWaveLabels: Record<string, string>,
  usePercentage: boolean
) {
  const expectAnswers = answerTypes.includes(questionType);

  if (expectAnswers && !tapAnswers?.length) return null;
  if (isEmpty(result)) return null;

  const existingLabels: string[] = [];

  const tapAnswersById =
    expectAnswers &&
    tapAnswers.reduce<Record<string, string>>((acc, answer) => {
      return {
        ...acc,
        [answer.attributes.id]: answer.attributes.option_text,
      };
    }, {});

  return Object.entries(result.results)
    .filter(([, values]) => values !== null)
    .map(([answerKey, waveValues]) => {
      const answerLabel = tapAnswersById?.[answerKey] || answerKey;
      const existingLabelCount: number = existingLabels.filter(
        (label) => label === answerLabel
      ).length;
      const label = !!existingLabelCount
        ? `${answerLabel} ${existingLabelCount + 1}`
        : answerLabel;
      existingLabels.push(answerLabel);
      return {
        id: label,
        data: waveValues
          .sort((a, b) => a.tap_survey_id - b.tap_survey_id)
          .map((wave) => {
            return {
              x: chartWaveLabels[wave.tap_survey_id],
              y: usePercentage ? wave.percentage : wave.value,
            };
          }),
      };
    });
}

export const useFormatResultsData = ({
  result,
  tapAnswers,
  questionType,
  chartWaveLabels,
  usePercentage,
}: FormatResultsDataProps) => {
  const { constants } = useAsset();
  const memoDeps = JSON.stringify({
    ...result,
    ...tapAnswers,
    ...chartWaveLabels,
    questionType,
    usePercentage,
  });

  const formattedData = useMemo(() => {
    return formatResultsData(
      [
        constants.TAP_ANSWER_TYPE_MULTIPLE_CHOICE,
        constants.TAP_ANSWER_TYPE_IMAGE_CHOICE,
        constants.TAP_ANSWER_TYPE_RANKING,
      ],
      result,
      tapAnswers,
      questionType,
      chartWaveLabels,
      usePercentage
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoDeps]);

  return formattedData;
};
